import { z } from "zod";

export const contactFormSchema = z.object({
  name: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s]+$/, "Name can only contain letters and spaces"),
  email: z.string().email("Please enter a valid email address"),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[6-9]\d{9}$/.test(val.replace(/\s/g, "")),
      "Please enter a valid Indian phone number"
    ),
  service: z.string().min(1, "Please select a service"),
  message: z
    .string()
    .max(1000, "Message must be less than 1000 characters")
    .optional(),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

export const loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export type LoginFormData = z.infer<typeof loginSchema>;

export const blogPostSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  slug: z.string().min(1, "Slug is required").max(200),
  excerpt: z.string().max(500).optional(),
  content: z.string().min(1, "Content is required"),
  coverImage: z
    .object({
      url: z.string().url(),
      publicId: z.string(),
    })
    .optional()
    .nullable(),
  author: z.string().optional(),
  tags: z.array(z.string()).optional(),
  category: z.string().optional(),
  status: z.enum(["draft", "published"]),
  seo: z
    .object({
      metaTitle: z.string().max(70).optional(),
      metaDescription: z.string().max(160).optional(),
      ogImage: z
        .object({ url: z.string().url(), publicId: z.string() })
        .optional()
        .nullable(),
    })
    .optional(),
});

export type BlogPostFormData = z.infer<typeof blogPostSchema>;

export const pageSectionSchema = z.object({
  sectionId: z.string(),
  type: z.enum([
    "hero",
    "stats",
    "features",
    "text",
    "services-list",
    "contact-info",
    "cta",
    "custom",
  ]),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  content: z.string().optional(),
  image: z
    .object({ url: z.string().url(), publicId: z.string() })
    .optional()
    .nullable(),
  videoUrl: z.string().optional(),
  items: z
    .array(
      z.object({
        title: z.string(),
        description: z.string().optional(),
        icon: z.string().optional(),
        image: z
          .object({ url: z.string().url(), publicId: z.string() })
          .optional()
          .nullable(),
        tags: z.array(z.string()).optional(),
        linkUrl: z.string().optional(),
        linkLabel: z.string().optional(),
      })
    )
    .optional(),
  buttons: z
    .array(
      z.object({
        label: z.string(),
        url: z.string(),
        variant: z.enum(["primary", "outline"]),
      })
    )
    .optional(),
  order: z.number(),
  isVisible: z.boolean(),
});

export const pageSchema = z.object({
  sections: z.array(pageSectionSchema),
  seo: z
    .object({
      title: z.string().max(70).optional(),
      description: z.string().max(160).optional(),
      keywords: z.array(z.string()).optional(),
      ogImage: z
        .object({ url: z.string().url(), publicId: z.string() })
        .optional()
        .nullable(),
    })
    .optional(),
});

export const siteSettingsSchema = z.object({
  siteName: z.string().min(1),
  tagline: z.string().optional(),
  logo: z
    .object({ url: z.string().url(), publicId: z.string() })
    .optional()
    .nullable(),
  favicon: z
    .object({ url: z.string().url(), publicId: z.string() })
    .optional()
    .nullable(),
  navigation: z
    .array(
      z.object({
        label: z.string(),
        href: z.string(),
        order: z.number(),
        isVisible: z.boolean(),
      })
    )
    .optional(),
  socialLinks: z
    .object({
      facebook: z.string().optional(),
      instagram: z.string().optional(),
      twitter: z.string().optional(),
      youtube: z.string().optional(),
      whatsapp: z.string().optional(),
    })
    .optional(),
  contactInfo: z
    .object({
      address: z.string().optional(),
      phone: z.string().optional(),
      email: z.string().optional(),
      mapUrl: z.string().optional(),
      coordinates: z
        .object({ lat: z.number(), lng: z.number() })
        .optional(),
    })
    .optional(),
  businessHours: z
    .object({
      weekday: z.string().optional(),
      weekend: z.string().optional(),
    })
    .optional(),
  footerText: z.string().optional(),
  footerDescription: z.string().optional(),
  ctaButtons: z
    .object({
      whatsapp: z
        .object({ label: z.string(), number: z.string() })
        .optional(),
      phone: z.object({ label: z.string(), number: z.string() }).optional(),
      email: z
        .object({ label: z.string(), address: z.string() })
        .optional(),
    })
    .optional(),
  analytics: z
    .object({
      gaId: z.string().optional(),
      gtagId: z.string().optional(),
    })
    .optional(),
});
